// The service that call the API to get the data from VEHICLE_API_URL

import { handleAxiosError } from '../../common/application/handleAxiosError';
import useParkstoryApi, { type ApiResponse, Target } from '../../common/data/parkstory/parkstory';
import { parkingListPopulate, parkingDetailPopulate, groupDetailPopulate, filter } from '../../common/data/strapi';
import useParkingConvertor, { type ApiParkingAttributes, type ChargerType, type Parking } from '../domain/parking';

import { type AbstractReturnType, type ApiEntities, type ApiEntity, mapApiEntities } from '@/common/domain/api';
import type { HttpError } from '@/common/domain/error';
import type { ApiParkingStats } from '@/common/domain/stats';
import { logException } from '@/common/utils/logger';
import useGroupConvertor, { type ApiGroup, type Group } from '@/group/domain/group';

const useParkingApi = () => {

    const { get, post, put, remove, convertApiResponse } = useParkstoryApi();
    const { apiParkingToParking, apiParkingsToParkings, parkingToApiParking } = useParkingConvertor;


    interface ParkingStatsReturnType extends AbstractReturnType {
        data: ApiParkingStats;
    }
    
    /*
     * Retreive basic stats from API
     * params withElectric and date optional in API (date default now, withElectric default false)
     */
    const apiGetParkingStats = async (parkingDocumentId: string, date: string, withElectric: boolean, vehicleTypeRef?: string): Promise<ParkingStatsReturnType> => {
        try {
            const response = await get(Target.Vehicle, `parkings/${parkingDocumentId}/usage?date=${date}&withElectric=${withElectric}${vehicleTypeRef ? '&vehicleType=' + vehicleTypeRef : '' }`);
            const simplifiedResponse: ParkingStatsReturnType = {
                data: response.data as ApiParkingStats,
                status: response.status
            };

            return simplifiedResponse;
        } catch (error) {
            logException(error, { parkingDocumentId });
            throw error;
        }
    };

    // Get parkings
    const apiGetParkings = async (): Promise<ApiResponse<Parking[]>> => {

        const sort = 'sort=createdAt:asc';

        try {
            const response = await get<ApiEntities<ApiParkingAttributes>>(
                Target.Vehicle,
                `parkings/?${parkingListPopulate}&${sort}`,
            );

            return convertApiResponse(response, apiParkingsToParkings);
        } catch (error) {
            logException(error);
            throw error;
        }
    };

    const apiGetChargerTypes = async (): Promise<ApiResponse<ChargerType[]>> => {

        const sort = 'sort=maxCharge:asc';

        try {
            const response = await get<ApiEntities<ChargerType>>(
                Target.Vehicle,
                'charger-types/findAll?' + sort,
            );

            return convertApiResponse(response, mapApiEntities<ChargerType>);
        } catch (error) {
            logException(error);
            throw error;
        }
    };


    const apiGetParkingById = async (id: string): Promise<ApiResponse<Parking>> => {
        try {

            const response = await get<ApiEntity<ApiParkingAttributes>>(
                Target.Vehicle,
                `parkings/${id}?${parkingDetailPopulate}`,
            );
            return convertApiResponse(response, apiParkingToParking);
        } catch (error) {
            throw (handleAxiosError(error as HttpError, 'Parking'));
        }
    };


    const apiGetParkingAdminsGroup = async (parkingId: string): Promise<ApiResponse<Group[]>> => {
        try {
            const filters = filter().equals('relatesTo.targetId', parkingId).build();
            const populate = groupDetailPopulate;
            const sort = 'sort=createdAt:asc';

            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${populate}&${filters}&${sort}`,
            );
            return (convertApiResponse(response, useGroupConvertor.apiGroupsToGroups));
        } catch (error) {
            logException(error, { parkingId });
            throw error;
        }
    };

    const apiUpdateParking = async (parking: Parking): Promise<ApiResponse<number>> => {
        try {
            const response = await put<ApiEntity<unknown>>(
                Target.Vehicle,
                `parkings/${parking.documentId ?? parking.id?.toString()}?${parkingDetailPopulate}`,
                parkingToApiParking(parking)
            );

            return {
                ...response,
                data: Number(response.data.id)
            };
        } catch (error) {
            logException(error, { parkingId: parking.documentId ?? parking.id });
            throw error;
        }
    };

    const apiCreateParking = async (parking: Parking): Promise<ApiResponse<string>> => {
        try {
            const response = await post<ApiEntity<unknown>>(
                Target.Vehicle,
                'parkings',
                parkingToApiParking(parking)
            );
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error) {
            logException(error, { parkingId: parking.documentId ?? parking.id });
            throw (handleAxiosError(error as HttpError, 'Parking'));
        }
    };

    const apiDeleteParking = async (parking: Parking): Promise<ApiResponse<Parking>> => {
        try {
            const response = await remove<ApiEntity<ApiParkingAttributes>>(
                Target.Vehicle,
                `parkings/${parking.documentId}`
            );

            return convertApiResponse(response, apiParkingToParking);
        } catch (error) {
            logException(error, { parkingId: parking.documentId ?? parking.id });
            throw (handleAxiosError(error as HttpError, 'Parking'));
        }
    };

    // Grant access to a group
    const apiGrantAccessToParking = async (
        parkingDocumentId: string,
        adminGroupDocumentId: string,
    ): Promise<ApiResponse<string>> => {
        try {
            const response = await post<ApiEntity<unknown>>(
                Target.Vehicle,
                `parkings/${parkingDocumentId}/grant`,
                { group: adminGroupDocumentId, verbs: ['READ', 'CREATE', 'UPDATE', 'DELETE', 'GRANT', 'REVOKE'] },
            );
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error) {
            logException(error, { parkingDocumentId, adminGroupDocumentId });
            throw error;
        }
    };
    
    // Grant read to a group
    const apiGrantReadAccessToParking = async (
        parkingDocumentId: string,
        memberGroupDocumentId: string,
    ): Promise<ApiResponse<string>> => {
        try {
            const response = await post<ApiEntity<unknown>>(
                Target.Vehicle,
                `parkings/${parkingDocumentId}/grant`,
                { group: memberGroupDocumentId, verbs: ['READ'] },
            );
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error) {
            logException(error, { parkingDocumentId, memberGroupDocumentId });
            throw error;
        }
    };


    return {
        apiCreateParking,
        apiDeleteParking,
        apiGetParkingStats,
        apiGetParkings,
        apiGetParkingAdminsGroup,
        apiGetParkingById,
        apiGrantAccessToParking,
        apiGrantReadAccessToParking,
        apiUpdateParking,
        apiGetChargerTypes
    };
};

export default useParkingApi;
